import type { PortalInvoiceDto } from '../infrastructure/billing.api';

// Pure display helpers for portal invoice statuses. No I/O, no React — safe to unit-test in isolation.
//
// Mirrors features/appointments/domain/appointmentStatus.ts's statusKey/statusTone pattern. Tone classes reuse
// the shared `--success`/`--warning`/`--danger`/`--info` semantic tokens (see packages/config/tailwind.preset.js)
// — the same tokens @doccareplus/ui's InvoiceBadge uses on the clinic side for Paid/PartiallyPaid/Overdue — so
// the portal reads with the same colour language without a cross-app import.

export type PortalInvoiceStatus = PortalInvoiceDto['status'];

/**
 * i18n key suffix for a status, mapping the C# PascalCase status name to the snake_case keys used in
 * portal.json's `billing.status.*`.
 */
export function invoiceStatusKey(status: string): string {
  return status.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
}

interface StatusTone {
  /** Tailwind classes for the badge pill (background + text colour). */
  tone: string;
}

const STATUS_TONES: Record<PortalInvoiceStatus, StatusTone> = {
  Issued: { tone: 'bg-info-bg text-info' },
  PartiallyPaid: { tone: 'bg-warning-bg text-warning' },
  Paid: { tone: 'bg-success-bg text-success' },
  Overdue: { tone: 'bg-danger-bg text-danger' },
};

const FALLBACK_TONE = 'bg-surface-2 text-text-3';

/** Tailwind tone classes for a status badge, falling back to a neutral tone for unknown values. */
export function invoiceStatusTone(status: string): string {
  return STATUS_TONES[status as PortalInvoiceStatus]?.tone ?? FALLBACK_TONE;
}
